import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { history } from "../store";

import MachineStatusBadge from "./MachineStatusBadge";
import MachineCashBadge from "./MachineCashBadge";
import { getMachineCash, getMachineCashColor } from "../utils/machines";
import {
  addUniversalUpdateSingleParameters,
  addUniversalUpdateSingleReboot,
} from "../actions/universalupdates";

import "../styles/MachinesAdminView.css";

class MachineList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      parametersConfirmShown: false,
      rebootConfirmShown: false,
      machineId: null,
      filterOpen: true,
      filters: {
        serial: "",
        customer: "",
        status: "",
        biller: "",
        pay: "",
      },
      // Local input values for immediate UI feedback
      inputValues: {
        serial: "",
        customer: "",
      },
      showButton: false,
      buttonPosition: { x: 0, y: 0 },
      selectedMachineId: null,
      filteredMachines: [], // Cache filtered results
      displayLimit: 100, // Initially show only 100 machines
      showAll: false, // Flag to show all machines
    };

    this.updateParameters = this.updateParameters.bind(this);
    this.rebootMachine = this.rebootMachine.bind(this);

    this.parametersConfirmShow = this.parametersConfirmShow.bind(this);
    this.parametersConfirmToggle = this.parametersConfirmToggle.bind(this);
    this.rebootConfirmShow = this.rebootConfirmShow.bind(this);
    this.rebootConfirmToggle = this.rebootConfirmToggle.bind(this);

    this.handleRightClick = this.handleRightClick.bind(this);
    this.toggleFilter = this.toggleFilter.bind(this);
    this.debounceTimer = null;
  }

  toggleFilter() {
    this.setState(prev => ({ filterOpen: !prev.filterOpen }));
  }

  componentDidMount() {
    // Initialize filtered machines
    this.updateFilteredMachines();
  }

  componentDidUpdate(prevProps, prevState) {
    // Re-filter when machines change or filters change
    if (
      prevProps.machines !== this.props.machines ||
      prevState.filters !== this.state.filters
    ) {
      this.updateFilteredMachines();
      // Reset to limited view when filters change
      if (prevState.filters !== this.state.filters) {
        this.setState({ showAll: false });
      }
    }
  }

  componentWillUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  updateFilteredMachines() {
    const filteredMachines = this.getFilteredMachines();
    this.setState({ filteredMachines });
  }

  goTo(id, e) {
    history.push(`/machines/${id}`);
  }

  handleRightClick(e, machineId) {
    e.preventDefault(); // Prevent the default context menu

    const elementRect = e.target.getBoundingClientRect();

    this.setState({
      showButton: true,
      buttonPosition: {
        x: elementRect.left + window.scrollX, // Adjust to account for scrolling
        y: elementRect.top + window.scrollY, // Adjust to account for scrolling
      },
      selectedMachineId: machineId,
    });
  }

  hideButton = () => {
    this.setState({ showButton: false, selectedMachineId: null });
  };

  handleButtonClick = () => {
    const { selectedMachineId } = this.state;
    if (selectedMachineId) {
      window.open(window.location.origin + `/machines/${selectedMachineId}`, '_blank', 'noopener,noreferrer');
      this.hideButton();
    }
  };

  handleFilterChange(filterKey, value) {
    // Update immediately for dropdowns (status, biller, pay)
    if (filterKey === 'status' || filterKey === 'biller' || filterKey === 'pay') {
      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [filterKey]: value,
        },
      }));
    } else {
      // For text inputs: update local input value immediately for UI feedback
      this.setState((prevState) => ({
        inputValues: {
          ...prevState.inputValues,
          [filterKey]: value,
        },
      }));

      // Debounce the actual filter state update by 400ms
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      this.debounceTimer = setTimeout(() => {
        this.setState((prevState) => ({
          filters: {
            ...prevState.filters,
            [filterKey]: value,
          },
        }));
      }, 400);
    }
  }

  clearFilters = () => {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.setState({
      filters: {
        serial: "",
        customer: "",
        status: "",
        biller: "",
        pay: "",
      },
      inputValues: {
        serial: "",
        customer: "",
      },
    });
  };

  getPayments(tipo) {
    switch (tipo) {
      case 0: {
        return "TPV + CASH";
      }
      case 1: {
        return "CASH";
      }
      case 2: {
        return "TPV";
      }
      case 3: {
        return "RFID";
      }
      case 4: {
        return "GRATIS";
      }
    }
  }

  renderLanguages(language, languages) {
    // Ensure both arguments are valid
    const safeLang = typeof language === "string" ? language : "es_ES";
    const list = Array.isArray(languages) ? languages : [];
    const mainLanguageCode = safeLang.split("_")[0];

    return (
      <div className="mach-admin-langs-container">
        <div className="mach-admin-lang-row base">
          <span className="lang-label">Base:</span>
          <img
            className="lang-flag-img"
            src={`/languages/${mainLanguageCode.toLowerCase()}.png`}
            alt={safeLang}
          />
        </div>
        <div className="mach-admin-lang-row display">
          <span className="lang-label">Display:</span>
          <div className="lang-flags-group">
            {list.slice(0, 3).map((lang, i) => {
              const languageCode = (lang?.Language || "es_ES").split("_")[0];
              return (
                <img
                  key={i}
                  className="lang-flag-img"
                  src={`/languages/${languageCode.toLowerCase()}.png`}
                  alt={lang?.Language || "es_ES"}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  handleParametersClick = (machine, event) => {
    event.stopPropagation();
    if (
      machine.Status !== "OPERATIVE" &&
      machine.Status !== "LOCKERS_FULL" &&
      machine.Status !== "SLEEP"
    ) {
      alert("Acción bloqueada! La máquina no está online.");
    } else {
      this.parametersConfirmShow(machine._id);
    }
  };

  updateParameters() {
    const { addUniversalUpdateSingleParameters } = this.props;

    addUniversalUpdateSingleParameters(this.state.machineId);
    this.parametersConfirmToggle();
  }

  parametersConfirmShow(id) {
    this.setState({
      parametersConfirmShown: true,
      machineId: id,
    });
  }

  parametersConfirmToggle() {
    this.setState({
      parametersConfirmShown: !this.state.parametersConfirmShown,
    });
  }

  handleRebootClick = (machine, event) => {
    event.stopPropagation();
    if (
      machine.Status !== "OPERATIVE" &&
      machine.Status !== "LOCKERS_FULL" &&
      machine.Status !== "SLEEP"
    ) {
      alert("Acción bloqueada! La máquina no está online.");
    } else {
      this.rebootConfirmShow(machine._id);
    }
  };

  rebootMachine() {
    const { addUniversalUpdateSingleReboot } = this.props;

    addUniversalUpdateSingleReboot(this.state.machineId);
    this.rebootConfirmToggle();
  }

  rebootConfirmShow(id) {
    this.setState({
      rebootConfirmShown: true,
      machineId: id,
    });
  }

  rebootConfirmToggle() {
    this.setState({ rebootConfirmShown: !this.state.rebootConfirmShown });
  }

  formatNightscanDate = (timestamp) => {
    if (!timestamp || typeof timestamp !== "string" || timestamp.length < 14) {
      return null;
    }

    const year = timestamp.substring(0, 4);
    const month = parseInt(timestamp.substring(4, 6), 10) - 1;
    const day = parseInt(timestamp.substring(6, 8), 10);
    const hour = parseInt(timestamp.substring(8, 10), 10);
    const minute = parseInt(timestamp.substring(10, 12), 10);
    const second = parseInt(timestamp.substring(12, 14), 10);

    return new Date(year, month, day, hour, minute, second);
  };

  isNightscanInSleepPeriod = (nightscanDate, sleepStart, sleepEnd) => {
    if (
      !nightscanDate ||
      !(nightscanDate instanceof Date) ||
      isNaN(nightscanDate)
    ) {
      return false;
    }

    const now = new Date();
    const today = new Date(now);
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    // Check if nightscanDate is from today or yesterday
    const isTodayOrYesterday =
      nightscanDate.toDateString() === today.toDateString() ||
      nightscanDate.toDateString() === yesterday.toDateString();

    if (!isTodayOrYesterday) return false;

    const startHours = Math.floor(sleepStart / 100);
    const startMinutes = sleepStart % 100;
    const endHours = Math.floor(sleepEnd / 100);
    const endMinutes = sleepEnd % 100;

    let startDate = new Date(nightscanDate);
    startDate.setHours(startHours, startMinutes, 0, 0);

    let endDate = new Date(nightscanDate);
    endDate.setHours(endHours, endMinutes, 0, 0);

    // If sleepStart is greater than sleepEnd, it means the sleep spans across midnight
    if (
      startHours > endHours ||
      (startHours === endHours && startMinutes > endMinutes)
    ) {
      // Adjust the dates to span midnight
      if (nightscanDate.toDateString() === yesterday.toDateString()) {
        // The nightscanDate is from yesterday, so we set the startDate as yesterday's date
        startDate = new Date(yesterday);
        startDate.setHours(startHours, startMinutes, 0, 0);

        // The endDate will be today at the sleepEnd time
        endDate = new Date(today);
        endDate.setHours(endHours, endMinutes, 0, 0);
      } else if (nightscanDate.toDateString() === today.toDateString()) {
        // The nightscanDate is from today, we need to check if it’s within the end range
        const previousDayStartDate = new Date(yesterday);
        previousDayStartDate.setHours(startHours, startMinutes, 0, 0);

        // End date remains as today's sleepEnd
        return (
          nightscanDate >= previousDayStartDate && nightscanDate <= endDate
        );
      }
    }

    // Normal case where sleepStart is less than or equal to sleepEnd (no midnight spanning)
    return nightscanDate >= startDate && nightscanDate <= endDate;
  };

  getZoneName(zone) {
    switch (zone) {
      case 0:
        return "GENERAL";
      case 1:
        return "BAQUEIRA";
      case 2:
        return "BERET";
      case 3:
        return "BONAIGUA";
      case 4:
        return "RUDA";
      case 5:
        return "BOSQUE";
      case 6:
        return "BOSQUE-CONSIGNAS";
      default:
        return "NONE";
    }
  }

  // Returns the effective status of a machine, resolving time-based OFFLINE
  // regardless of the stored Status field (which never contains 'OFFLINE')
  getEffectiveStatus(machine) {
    if (!machine.Last_signal) return machine.Status || '';
    const now = Date.now();
    const lastSignalTime = Date.parse(machine.Last_signal);
    const diffInSeconds = (now - lastSignalTime) / 1000;
    if (diffInSeconds >= 60 * 5) return 'OFFLINE';
    return machine.Status || '';
  }

  getUpdaterStatus(lastUpdaterSignal) {
    if (!lastUpdaterSignal) return "🟢"; // Default to red if no signal

    const lastSignalDate = new Date(lastUpdaterSignal);
    const now = new Date();
    const diffInMinutes = (now - lastSignalDate) / (1000 * 60);

    return diffInMinutes > 60 ? "🟢" : "🟢";
  }

  getFilteredMachines() {
    const { filters } = this.state;
    const { machines, category } = this.props;
    const { serial, customer, status, biller, pay } = filters;

    return machines.filter((machine) => {
      // Apply the Serial filter
      if (
        serial &&
        !machine.Serial.toLowerCase().includes(serial.trim().toLowerCase())
      ) {
        return false;
      }

      // Apply the Customer or Contract Zone filter based on category
      if (customer) {
        if (category === "BAQUEIRA") {
          // Apply the Contract Zone filter
          if (
            !machine.Contract ||
            !machine.Contract.Zone ||
            !this.getZoneName(machine.Contract.Zone)
              .toLowerCase()
              .includes(customer.toLowerCase())
          ) {
            return false;
          }
        } else {
          // Apply the Customer filter
          if (
            !machine.Customer ||
            !machine.Customer.Fullname.toLowerCase().includes(
              customer.toLowerCase()
            )
          ) {
            return false;
          }
        }
      }

      // Apply the Status filter
      // OFFLINE is a derived status based on time since last signal, not a stored value,
      // so we compute the effective status before comparing.
      if (status) {
        const effectiveStatus = this.getEffectiveStatus(machine);
        if (!effectiveStatus.toLowerCase().includes(status.toLowerCase())) {
          return false;
        }
      }

      // Apply the Biller filter
      if (
        biller &&
        !getMachineCash(machine).toLowerCase().includes(biller.toLowerCase())
      ) {
        return false;
      }

      // Apply the Pay filter
      if (pay && machine.PaymentMethods !== parseInt(pay, 10)) {
        return false;
      }

      // If all conditions pass, include the machine in the filtered list
      return true;
    });
  }

  render() {
    const { inputValues, filteredMachines, filterOpen } = this.state;
    const { filters } = this.state;
    const { machines, auth, role, category, type } = this.props;

    if (!machines) {
      return <div></div>;
    }

    return (
      <div>
        {/* Collapsible Filter Panel */}
        <div className="mach-admin-filter-card">
          <div className="mach-admin-filter-header" onClick={this.toggleFilter}>
            <h3><i className="fas fa-filter"></i> Filtros</h3>
            <i className={`fas fa-chevron-down toggle-icon ${filterOpen ? 'open' : ''}`}></i>
          </div>
          {filterOpen && (
            <div className="mach-admin-filter-body">
              <div className="filter-grid">
                <div className="filter-group">
                  <label htmlFor="filter-serial">Identificador</label>
                  <input
                    id="filter-serial"
                    type="text"
                    placeholder="Buscar por serial..."
                    value={inputValues.serial}
                    onChange={(e) =>
                      this.handleFilterChange("serial", e.target.value)
                    }
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="filter-customer">
                    {category === "BAQUEIRA" ? "Zona" : "Cliente"}
                  </label>
                  <input
                    id="filter-customer"
                    type="text"
                    placeholder={`Buscar por ${category === "BAQUEIRA" ? "zona" : "cliente"}...`}
                    value={inputValues.customer}
                    onChange={(e) =>
                      this.handleFilterChange("customer", e.target.value)
                    }
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="filter-status">Estado</label>
                  <select
                    id="filter-status"
                    value={filters.status}
                    onChange={(e) =>
                      this.handleFilterChange("status", e.target.value)
                    }
                  >
                    <option value="">Todos</option>
                    <option value="OPERATIVE">ONLINE</option>
                    <option value="OFFLINE">OFFLINE</option>
                    <option value="SLEEP">SLEEP</option>
                    <option value="NIGHT_SCAN">NIGHTSCAN</option>
                    <option value="MAINTENANCE">MANTENIMIENTO</option>
                    <option value="LOCKERS_FULL">LOCKERS LLENOS</option>
                    <option value="TICKET_EMPTY">SIN TICKET</option>
                    <option value="KERONG_OTA">OTA KERONG</option>
                    <option value="BOOTLOADER_OTA">OTA BOOTLOADER</option>
                    <option value="BLE_OFF">POWER DOWN</option>
                  </select>
                </div>
                {category !== "BAQUEIRA" && (
                  <>
                    <div className="filter-group">
                      <label htmlFor="filter-pay">Tipo de Pago</label>
                      <select
                        id="filter-pay"
                        value={filters.pay}
                        onChange={(e) =>
                          this.handleFilterChange("pay", e.target.value)
                        }
                      >
                        <option value="">Todos</option>
                        <option value="0">TPV + CASH</option>
                        <option value="1">CASH</option>
                        <option value="2">TPV</option>
                        <option value="3">RFID</option>
                        <option value="4">GRATIS</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-biller">Billetero</label>
                      <select
                        id="filter-biller"
                        value={filters.biller}
                        onChange={(e) =>
                          this.handleFilterChange("biller", e.target.value)
                        }
                      >
                        <option value="">Todos</option>
                        <option value="OK">OK</option>
                        <option value="FULL">FULL</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Column Headers */}
        <div className="mach-admin-list-header">
          <div className="mach-header-col col-id">Identificador</div>
          <div className="mach-header-col col-customer">{category === "BAQUEIRA" ? "Zona" : "Cliente"}</div>
          <div className="mach-header-col col-status">Estado</div>
          {category !== "BAQUEIRA" && <div className="mach-header-col col-langs">Idiomas</div>}
          <div className="mach-header-col col-passwords">Contraseñas</div>
          {role === "ADMIN" && <div className="mach-header-col col-versions">Versiones</div>}
          {category !== "BAQUEIRA" && <div className="mach-header-col col-payments">Pagos</div>}
          {role === "ADMIN" && <div className="mach-header-col col-nightscan">Nightscan</div>}
          <div className="mach-header-col col-pc">PC</div>
          {role === "ADMIN" && <div className="mach-header-col col-actions">Acciones</div>}
        </div>

        {/* Machine Cards */}
        <div className="mach-admin-machine-list">
          {filteredMachines.length > 0 ? (
            <>
              {(this.state.showAll 
                ? filteredMachines 
                : filteredMachines.slice(0, this.state.displayLimit)
              ).map((machine) => {
                // Create a new Date object using the parsed parts
                const nightscanDate = this.formatNightscanDate(
                  machine.NightScanTimeStampEnd
                );

                const isInSleepPeriod = this.isNightscanInSleepPeriod(
                  nightscanDate,
                  machine.SleepStart,
                  machine.SleepEnd
                );

                // Get CSS-friendly status (use raw status, handle OFFLINE case)
                const now = Date.now();
                const lastSignalTime = machine.Last_signal ? Date.parse(machine.Last_signal) : 0;
                const diffInSeconds = (now - lastSignalTime) / 1000;
                const statusForCSS = diffInSeconds >= 60 * 5 ? 'OFFLINE' : (machine.Status || 'UNKNOWN');

                return (
                  <div
                    key={machine._id}
                    className="mach-admin-machine-card"
                    onClick={(e) => this.goTo(machine._id, e)}
                    onContextMenu={(e) => this.handleRightClick(e, machine._id)}
                  >
                    <div className="mach-admin-machine-row">
                      {/* Status bar */}
                      <div className={`mach-admin-status-bar ${statusForCSS}`}></div>
                      
                      {/* Content */}
                      <div className="mach-admin-machine-content">
                        {/* Machine ID */}
                        <div className="mach-admin-machine-id">
                          <div className="mach-admin-serial">{machine.Serial}</div>
                          <div className="mach-admin-locker-range">
                            {`${machine.FirstIndex} - ${parseInt(machine.LastIndex) + parseInt(machine.FirstIndex) - 1}`}
                            <br />({machine.LastIndex} Taquillas)
                          </div>
                        </div>

                        {/* Customer/Zone */}
                        <div className="mach-admin-machine-customer notranslate">
                          {machine.Customer ? (
                            category === "BAQUEIRA" ? (
                              <div className="mach-admin-customer-name">{this.getZoneName(machine.Contract.Zone)}</div>
                            ) : (
                              <>
                                <div className="mach-admin-customer-name">{machine.Customer.Fullname}</div>
                                <div className="mach-admin-zone">
                                  {(() => {
                                    const zoneObj = machine.Customer.Zones?.find(
                                      (z) => z.Zone === machine.Contract.Zone
                                    );
                                    return zoneObj
                                      ? `Zona ${zoneObj.Zone} - ${zoneObj.Description}`
                                      : `Zona ${machine.Contract.Zone}`;
                                  })()}
                                </div>
                                {machine.QRActive && (
                                  <div className="mach-admin-qr-badge">
                                    <i className="fa fa-qrcode"></i>
                                    {machine.QRPercentage}%
                                  </div>
                                )}
                              </>
                            )
                          ) : (
                            <div className="mach-admin-customer-name">None</div>
                          )}
                        </div>

                        {/* Status */}
                        <div className="mach-admin-machine-status">
                          <MachineStatusBadge machine={machine} />
                          <div className="mach-admin-status-line">
                            <i className="fas fa-clock"></i>
                            {machine.Last_signal
                              ? new Date(machine.Last_signal).toLocaleString()
                              : "—"}
                          </div>
                          <div className="mach-admin-status-line">
                            <span className="status-label">Sleep:</span>
                            {`${machine?.SleepStart?.substring(0, 2)}:${machine?.SleepStart?.substring(2)} - ${machine?.SleepEnd?.substring(0, 2)}:${machine?.SleepEnd?.substring(2)}`}
                          </div>
                          <div className="mach-admin-status-line">
                            <span className="status-label">Updater:</span>
                            {this.getUpdaterStatus(machine.Last_updater_signal)}
                          </div>
                        </div>

                        {/* Languages */}
                        {category !== "BAQUEIRA" && (
                          <div className="mach-admin-machine-langs">
                            {machine?.SupportedLanguages
                              ? this.renderLanguages(machine.Language, machine.SupportedLanguages)
                              : "—"}
                          </div>
                        )}

                        {/* Passwords */}
                        <div className="mach-admin-machine-passwords">
                          {category !== "BAQUEIRA" && (
                            <>
                              <div className="mach-admin-pwd-item">
                                <i className="fas fa-cogs"></i>
                                <span className="pwd-label">Máquina:</span>
                                <span className="pwd-value">{machine.PasswordMachine}</span>
                              </div>
                              <div className="mach-admin-pwd-item">
                                <i className="fas fa-money-bill-wave"></i>
                                <span className="pwd-label">Cash:</span>
                                <span className="pwd-value">{machine.PasswordFund}</span>
                              </div>
                            </>
                          )}
                          {type !== "CASH" && (
                            <div className="mach-admin-pwd-item">
                              <i className="fas fa-lock"></i>
                              <span className="pwd-label">Universal:</span>
                              <span className="pwd-value">{machine.PasswordCash}</span>
                            </div>
                          )}
                          {role === "ADMIN" && (
                            <>
                              <div className="mach-admin-pwd-item">
                                <i className="fas fa-user-shield"></i>
                                <span className="pwd-label">Master:</span>
                                <span className="pwd-value">{machine.PasswordMaster}</span>
                              </div>
                              <div className="mach-admin-pwd-item">
                                <i className="fas fa-clipboard-list"></i>
                                <span className="pwd-label">QC:</span>
                                <span className="pwd-value">{machine.PasswordQC}</span>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Versions */}
                        {role === "ADMIN" && (
                          <div className="mach-admin-machine-versions">
                            <div className="mach-admin-version-item">
                              <span className="label">SW:</span>
                              <span className="value">{machine.Sw_version}</span>
                            </div>
                            <div className="mach-admin-version-item">
                              <span className="label">FW:</span>
                              <span className="value">{machine.Fw_version}</span>
                            </div>
                            <div className="mach-admin-version-item">
                              <span className="label">UP:</span>
                              <span className="value">{machine.Up_version}</span>
                            </div>
                            <div className="mach-admin-version-item">
                              <span className="label">ESP:</span>
                              <span className="value">{machine.ESP32_version}</span>
                            </div>
                          </div>
                        )}

                        {/* Payments */}
                        {category !== "BAQUEIRA" && (
                          <div className="mach-admin-machine-payments">
                            <div className="mach-admin-payment-type">
                              {this.getPayments(machine.PaymentMethods)}
                            </div>
                            {(machine.PaymentMethods === 0 || machine.PaymentMethods === 1) && (
                              <MachineCashBadge machine={machine} />
                            )}
                          </div>
                        )}

                        {/* Nightscan */}
                        {role === "ADMIN" && (
                          <div className="mach-admin-machine-nightscan">
                            <div className={`mach-admin-nightscan-badge ${isInSleepPeriod ? 'success' : 'danger'}`}>
                              {nightscanDate ? nightscanDate.toLocaleString() : "—"}
                            </div>
                          </div>
                        )}

                        {/* PC Info */}
                        <div className="mach-admin-machine-pc">
                          <div className="mach-admin-pc-line temp">{machine.CoreTemp}</div>
                          <div className="mach-admin-pc-line mono">{machine.Anydesk}</div>
                          <div className="mach-admin-pc-line mono">{machine.IP}</div>
                          {role === "ADMIN" && (
                            machine.OS === 2 ? (
                              <div className="mach-admin-os-badge">
                                <i className="fa-brands fa-ubuntu"></i>
                                Ubuntu
                              </div>
                            ) : (
                              <div className="mach-admin-os-badge">
                                <i className="fa-brands fa-raspberry-pi"></i>
                                Raspbian
                              </div>
                            )
                          )}
                        </div>

                        {/* Actions */}
                        {role === "ADMIN" && (
                          <div className="mach-admin-machine-actions">
                            <button
                              className="mach-admin-card-action-btn update"
                              onClick={(event) => this.handleParametersClick(machine, event)}
                            >
                              <i className="fas fa-database"></i>
                              Actualizar
                            </button>
                            <button
                              className="mach-admin-card-action-btn reboot"
                              onClick={(event) => this.handleRebootClick(machine, event)}
                            >
                              <i className="fas fa-plug"></i>
                              Reiniciar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="mach-admin-empty">
              <i className="fas fa-server"></i>
              <p>No se encontraron máquinas</p>
            </div>
          )}
        </div>
        
        {/* Show "Load More" button if there are more machines to display */}
        {!this.state.showAll && filteredMachines.length > this.state.displayLimit && (
          <div className="load-more-section">
            <div className="count-info">
              Mostrando {this.state.displayLimit} de {filteredMachines.length} máquinas
            </div>
            <br />
            <button
              className="load-more-btn"
              onClick={() => this.setState({ showAll: true })}
            >
              <i className="fas fa-chevron-down"></i>
              Mostrar todas ({filteredMachines.length})
            </button>
          </div>
        )}
        
        {/* Show summary when all are displayed */}
        {this.state.showAll && filteredMachines.length > this.state.displayLimit && (
          <div className="load-more-section">
            <div className="results-count">
              Mostrando todas las {filteredMachines.length} máquinas
            </div>
          </div>
        )}
        
        <div>
          <Modal
            isOpen={this.state.parametersConfirmShown}
            toggle={this.parametersConfirmToggle}
          >
            <ModalHeader toggle={this.parametersConfirmToggle}>
              Confirmar
            </ModalHeader>
            <ModalBody>
              <p>¿Estás seguro de actualizar los parámetros?</p>
              <br></br>
              <p>
                -La máquina se actualizará interrumpiendo los procesos y saldrá
                un mensaje en pantalla para reiniciar la máquina una vez
                finalizado el proceso.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button color="primary mr-1" onClick={this.updateParameters}>
                Actualizar
              </Button>
              <Button color="secondary" onClick={this.parametersConfirmToggle}>
                Cancelar
              </Button>
            </ModalFooter>
          </Modal>
        </div>
        <div>
          <Modal
            isOpen={this.state.rebootConfirmShown}
            toggle={this.rebootConfirmToggle}
          >
            <ModalHeader toggle={this.rebootConfirmToggle}>
              Confirmar
            </ModalHeader>
            <ModalBody>
              <p>¿Estás seguro de reiniciar la máquina?</p>
              <br></br>
              <p>-La máquina se reiniciará interrumpiendo los procesos.</p>
            </ModalBody>
            <ModalFooter>
              <Button color="primary mr-1" onClick={this.rebootMachine}>
                Reiniciar
              </Button>
              <Button color="secondary" onClick={this.rebootConfirmToggle}>
                Cancelar
              </Button>
            </ModalFooter>
          </Modal>
        </div>
        {this.state.showButton && (
          <div
            style={{
              position: "absolute",
              top: `${this.state.buttonPosition.y}px`,
              left: `${this.state.buttonPosition.x}px`,
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <button
              onClick={e => { e.stopPropagation(); e.preventDefault(); this.handleButtonClick() }}
              style={{
                background: 'linear-gradient(135deg, #022040, #0a4080)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <i className="fas fa-external-link-alt"></i>
              Abrir en pestaña nueva
            </button>
            <button
              onClick={this.hideButton}
              style={{
                background: '#f1f5f9',
                color: '#334155',
                border: '1px solid #dde3ec',
                borderRadius: 8,
                padding: '6px 16px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  role: state.auth.role,
  type: state.auth.type,
  fullname: state.auth.fullname,
  category: state.auth.category,
});

const mapDispatchToProps = (dispatch) => ({
  addUniversalUpdateSingleParameters: bindActionCreators(
    addUniversalUpdateSingleParameters,
    dispatch
  ),
  addUniversalUpdateSingleReboot: bindActionCreators(
    addUniversalUpdateSingleReboot,
    dispatch
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(MachineList);
